// Copyright 2025, University of Maryland, All Rights Reserved
import type { KbSchema, KnowledgeBaseItem } from '../types'
import type { KnowledgeBaseSettings } from '../hooks/useKnowledgeBaseSettings'
import type { ItemBrowserResult } from '../hooks/useItemBrowser'
import type { AddItemFormSubmission } from '../items/AddItemForm'
import { AddItemForm } from '../items/AddItemForm'
import { ItemFilters } from '../items/ItemFilters'
import type { ItemListUpdatePayload } from '../items/ItemList'
import { ItemList } from '../items/ItemList'
import { EmptyState } from '../ui/EmptyState'
import { PaginationControls } from '../ui/PaginationControls'

interface ItemsTabProps {
  browser: ItemBrowserResult
  items: KnowledgeBaseItem[]
  settings: KnowledgeBaseSettings
  schema?: KbSchema | null
  saving: boolean
  readOnly: boolean
  disableActions: boolean
  onCreateItem: (submission: AddItemFormSubmission) => Promise<void>
  onUpdateItem: (item: KnowledgeBaseItem, payload: ItemListUpdatePayload) => Promise<void>
  onDeleteItem: (item: KnowledgeBaseItem) => Promise<void>
  onError: (message: string) => void
}

export function ItemsTab({
  browser,
  items,
  settings,
  schema,
  saving,
  readOnly,
  disableActions,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
  onError,
}: ItemsTabProps): JSX.Element {
  const showPagination = browser.paginatedItems.length > 0 && browser.pagination.totalPages > 1

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3">
        <ItemFilters
          filters={browser.filters}
          items={browser.filteredItems}
          totalItems={items.length}
          onTextChange={text => browser.updateText(text)}
        />

        {!readOnly && (
          <AddItemForm
            disabled={disableActions}
            saving={saving}
            settings={settings}
            schema={schema}
            onSubmit={onCreateItem}
            onError={onError}
          />
        )}
      </div>

      <section className="flex flex-col">
        {browser.paginatedItems.length === 0 ? (
          items.length === 0 ? (
            <EmptyState
              title="No guidance yet"
              description={readOnly
                ? 'A workspace admin has not added guidance items yet. Once added, the AI applies them to every document analyzed in this workspace.'
                : 'Guidance items tell the AI what to look for in documents. Click "Add Guidance Item" above to write your first one — a title and a short description of the issue is all it takes.'}
            />
          ) : (
            <EmptyState
              title="No matching items"
              description="No guidance matches your search. Clear the filter to see all items."
            />
          )
        ) : (
          <ItemList
            items={browser.paginatedItems}
            settings={settings}
            schema={schema}
            saving={saving}
            readOnly={readOnly}
            disabled={disableActions}
            onUpdate={onUpdateItem}
            onDelete={onDeleteItem}
            onError={onError}
          />
        )}
      </section>

      {showPagination && (
        <div className="sticky bottom-0 -mx-4 sm:-mx-6 mt-4 border-t bg-background px-4 sm:px-6 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)]">
          <PaginationControls
            currentPage={browser.pagination.currentPage}
            totalPages={browser.pagination.totalPages}
            showing={browser.paginatedItems.length}
            totalItems={browser.filteredItems.length}
            onPageChange={browser.pagination.setPage}
          />
        </div>
      )}
    </div>
  )
}
